import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment.dev';

@Injectable({ providedIn: 'root' })
export class StorageIntegrityService {
  /** Keys whose values are protected against manual modification */
  readonly PROTECTED_KEYS: readonly string[] = [
    'BaseUrl',
    'currentCompany',
    'isLoggedIn',
    'isCustomerLogin',
    'Applications'
  ];

  private readonly STATE_KEY = 'app_state';
  private readonly HASH_KEY  = 'app_state_hash';

  private readonly CACHE_TTL_MS = 10_000; // 10-second validation cache
  private lastValidationTime   = 0;
  private lastValidationResult = true;

  private tamperingHandled = false;

  constructor(private router: Router) {}

  // ─── Public API ───────────────────────────────────────────────────────────

  /** Snapshot current protected keys with SHA-256. Call after login / company switch. */
  async saveState(): Promise<void> {
    const state = this.collectLiveState();
    const json  = this.deterministicSerialize(state);
    const hash  = await this.sha256(json);
    localStorage.setItem(this.STATE_KEY, json);
    localStorage.setItem(this.HASH_KEY,  hash);
  }

  /**
   * Validate that no protected key has been externally modified.
   * @param force  Bypass the 10-second cache (use on startup and route changes).
   */
  async validateIntegrity(force = false): Promise<boolean> {
    if (!crypto?.subtle) return true; // Web Crypto not available — skip

    const now = Date.now();
    if (!force && now - this.lastValidationTime < this.CACHE_TTL_MS) {
      return this.lastValidationResult;
    }

    const result = await this.performValidation();
    this.lastValidationTime   = now;
    this.lastValidationResult = result;
    return result;
  }

  /** Validate and call handleTampering() on failure. */
  async checkIntegrity(force = false): Promise<boolean> {
    const ok = await this.validateIntegrity(force);
    if (!ok) this.handleTampering();
    return ok;
  }

  /**
   * Remove the stored snapshot.
   * MUST be called BEFORE localStorage.clear() on logout to prevent
   * a false-positive tamper alert on the next login.
   */
  clearState(): void {
    localStorage.removeItem(this.STATE_KEY);
    localStorage.removeItem(this.HASH_KEY);
    this.lastValidationTime   = 0;
    this.lastValidationResult = true;
    this.tamperingHandled     = false;
  }

  /** Wipe storage and redirect to login. Idempotent. */
  handleTampering(): void {
    if (this.tamperingHandled) return;
    this.tamperingHandled = true;

    console.error('[StorageIntegrity] Tampering detected — signing out');

    // Fire signout API (keepalive — survives page navigation)
    const company  = this.readActiveCompany();
    const token    = this.readToken(company);
    const userName = this.readUserName(company);

    fetch(`${environment.CAS_BASE_RUL}api/CAS/signout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Client-Code': 'CRM' },
      credentials: 'include',
      body: JSON.stringify({ userName, tokens: { [company]: token } }),
      keepalive: true
    }).catch(() => {});

    localStorage.clear();
    this.router.navigate(['/auth/login'], { queryParams: { reason: 'security' } });
  }

  // ─── Private ──────────────────────────────────────────────────────────────

  private async performValidation(): Promise<boolean> {
    const storedJson = localStorage.getItem(this.STATE_KEY);
    const storedHash = localStorage.getItem(this.HASH_KEY);

    // No snapshot means either:
    //   a) Fresh session (user never logged in yet), or
    //   b) Normal logout (clearState() was called before localStorage.clear())
    // Both are legitimate — allow.
    if (!storedJson || !storedHash) return true;

    // Step 1: verify the snapshot hash itself hasn't been tampered with
    const recomputedHash = await this.sha256(storedJson);
    if (recomputedHash !== storedHash) {
      console.warn('[StorageIntegrity] Hash mismatch — snapshot was modified');
      return false;
    }

    // Step 2: verify every protected live value matches the snapshot
    let storedState: Record<string, string | null>;
    try {
      storedState = JSON.parse(storedJson);
    } catch {
      console.warn('[StorageIntegrity] Could not parse stored state');
      return false;
    }

    const liveState = this.collectLiveState();

    for (const key of this.PROTECTED_KEYS) {
      const storedVal = storedState[key] ?? null;
      const liveVal   = liveState[key];

      if (liveVal !== storedVal) {
        console.warn(
          `[StorageIntegrity] Key "${key}" tampered — snapshot: "${storedVal}", live: "${liveVal}"`
        );
        return false;
      }
    }

    return true;
  }

  private collectLiveState(): Record<string, string | null> {
    const state: Record<string, string | null> = {};
    for (const key of this.PROTECTED_KEYS) {
      state[key] = localStorage.getItem(key);
    }
    return state;
  }

  /** Sorted keys for deterministic JSON → stable hash across calls */
  private deterministicSerialize(obj: Record<string, string | null>): string {
    const sorted: Record<string, string | null> = {};
    for (const key of Object.keys(obj).sort()) {
      sorted[key] = obj[key];
    }
    return JSON.stringify(sorted);
  }

  private async sha256(input: string): Promise<string> {
    const data        = new TextEncoder().encode(input);
    const hashBuffer  = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hashBuffer))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');
  }

  private readActiveCompany(): string {
    return localStorage.getItem('currentCompany') || 'unity';
  }

  private readToken(company: string): string {
    return localStorage.getItem(`tibss.${company}-Token`) || '';
  }

  private readUserName(company: string): string {
    try {
      const raw = localStorage.getItem(`tibss.${company}-userDetails`);
      return raw ? JSON.parse(raw)?.userName || '' : '';
    } catch {
      return '';
    }
  }

}

